import React, { useState, useEffect } from 'react'
import { GalleryImage } from '../../types'
import { galleryAPI } from '../../services/api'

interface GalleryManagementProps {
  onNotification?: (type: 'success' | 'error', message: string) => void
}

const GalleryManagement: React.FC<GalleryManagementProps> = ({ onNotification }) => {
  const [galleryImages, setGalleryImages] = useState<GalleryImage[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [showAddForm, setShowAddForm] = useState(false)
  const [newImage, setNewImage] = useState({
    image_name: '',
    image_file: ''
  })
  const [uploadPreview, setUploadPreview] = useState<string>('')

  // Load gallery images on component mount
  useEffect(() => {
    loadGalleryImages()
  }, [])

  const loadGalleryImages = async () => {
    setIsLoading(true)
    try {
      const images = await galleryAPI.getAdminImages()
      setGalleryImages(Array.isArray(images) ? images : [])
      
      // Check if we're using fallback images
      if (images && images.length > 0 && images[0].image_file?.includes('/assets/')) {
        onNotification?.('success', 'Gallery loaded (using fallback images - backend may be unavailable)')
      }
    } catch (error) {
      console.error('Error loading gallery images:', error)
      onNotification?.('error', 'Failed to load gallery images')
      setGalleryImages([]) // Set empty array on error
    } finally {
      setIsLoading(false)
    }
  }

  // Listen for gallery updates from other tabs/components
  useEffect(() => {
    const handleGalleryUpdate = () => {
      console.log('Gallery updated via custom event, refreshing admin gallery...')
      loadGalleryImages()
    }

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'galleryImages') {
        console.log('Gallery updated via storage event, refreshing admin gallery...')
        loadGalleryImages()
      }
    }

    window.addEventListener('galleryUpdated', handleGalleryUpdate)
    window.addEventListener('storage', handleStorageChange)

    return () => {
      window.removeEventListener('galleryUpdated', handleGalleryUpdate)
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [])

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      // Check file size (5MB limit)
      const maxSize = 5 * 1024 * 1024 // 5MB in bytes
      if (file.size > maxSize) {
        onNotification?.('error', 'File size too large. Please select an image smaller than 5MB.')
        e.target.value = '' // Clear the input
        return
      }
      
      // Check file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
      if (!allowedTypes.includes(file.type)) {
        onNotification?.('error', 'Invalid file type. Please select a JPEG, PNG, or WebP image.')
        e.target.value = '' // Clear the input
        return
      }
      
      const reader = new FileReader()
      reader.onload = (e) => {
        setUploadPreview(e.target?.result as string)
        setNewImage(prev => ({ 
          ...prev, 
          image_name: file.name,
          image_file: e.target?.result as string 
        }))
      }
      reader.readAsDataURL(file)
    }
  }

  const handleAddImage = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validate form data
    if (!newImage.image_name.trim()) {
      onNotification?.('error', 'Please enter an image name')
      return
    }
    
    if (!newImage.image_file.trim()) {
      onNotification?.('error', 'Please upload an image file')
      return
    }
    
    setIsLoading(true)
    try {
      const imageData = {
        image_name: newImage.image_name.trim(),
        image_file: newImage.image_file.trim()
      }
      
      await galleryAPI.uploadImage(imageData)
      
      // Reset form
      setNewImage({ image_name: '', image_file: '' })
      setUploadPreview('')
      setShowAddForm(false)
      
      // Reload gallery images to show the new image
      await loadGalleryImages()
      
      // Trigger custom event to notify other components
      window.dispatchEvent(new CustomEvent('galleryUpdated'))
      
      onNotification?.('success', 'Image added successfully')
    } catch (error) {
      console.error('Error adding image:', error)
      
      // Handle specific error types
      if ((error as any).response?.status === 413) {
        onNotification?.('error', 'Image file is too large. Please select a smaller image (under 5MB).')
      } else if ((error as any).response?.status === 400) {
        onNotification?.('error', 'Invalid image format. Please select a valid image file.')
      } else if ((error as any).response?.status >= 500) {
        onNotification?.('error', 'Server error. Please try again later.')
      } else {
        onNotification?.('error', 'Failed to add image. Please try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleDeleteImage = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return
    
    setIsLoading(true)
    try {
      const result = await galleryAPI.deleteImage(id.toString())
      
      // Reload gallery images to reflect the deletion
      await loadGalleryImages()
      
      // Trigger custom event to notify other components
      window.dispatchEvent(new CustomEvent('galleryUpdated'))
      
      // Check if deletion was handled by fallback
      if (result.message && result.message.includes('local storage')) {
        onNotification?.('success', 'Image deleted successfully (from local storage)')
      } else {
        onNotification?.('success', 'Image deleted successfully')
      }
    } catch (error) {
      console.error('Error deleting image:', error)
      onNotification?.('error', 'Failed to delete image')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="admin-section">
      <div className="admin-section-header">
        <h2>Gallery Management</h2>
        <div className="header-actions">
          <button 
            className="btn btn-secondary"
            onClick={loadGalleryImages}
            disabled={isLoading}
          >
            {isLoading ? 'Loading...' : 'Refresh'}
          </button>
          <button 
            className="btn btn-primary"
            onClick={() => setShowAddForm(!showAddForm)}
          >
            {showAddForm ? 'Cancel' : 'Add Image'}
          </button>
        </div>
      </div>

      {showAddForm && (
        <div className="admin-form">
          <form onSubmit={handleAddImage}>
            <div className="form-group">
              <label>Upload Image File *</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                className="file-input"
                required
              />
              {uploadPreview && (
                <div className="image-preview">
                  <img src={uploadPreview} alt="Preview" style={{maxWidth: '200px', maxHeight: '200px'}} />
                </div>
              )}
            </div>
            
            <div className="form-group">
              <label>Image Name *</label>
              <input
                type="text"
                value={newImage.image_name}
                onChange={(e) => setNewImage({...newImage, image_name: e.target.value})}
                placeholder="Enter image name"
                required
              />
            </div>
            
            <div className="form-actions">
              <button 
                type="submit" 
                className="btn btn-primary" 
                disabled={!newImage.image_name.trim() || !newImage.image_file.trim() || isLoading}
              >
                {isLoading ? 'Adding...' : 'Add Image'}
              </button>
              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={() => {
                  setShowAddForm(false)
                  setNewImage({ image_name: '', image_file: '' })
                  setUploadPreview('')
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {isLoading ? (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading gallery images...</p>
        </div>
      ) : (
        <div className="gallery-grid-admin">
          {Array.isArray(galleryImages) && galleryImages.length > 0 ? (
            galleryImages.map((image) => (
              <div key={image.id} className="gallery-item-admin">
                <div className="gallery-item-image">
                  <img src={image.image_file} alt={image.image_name} />
                </div>
                <div className="gallery-item-info">
                  <h4>{image.image_name}</h4>
                  <p className="image-date">Added: {new Date(image.created_at).toLocaleDateString()}</p>
                </div>
                <div className="gallery-item-actions">
                  <button 
                    className="btn btn-sm btn-danger"
                    onClick={() => handleDeleteImage(image.id)}
                    disabled={isLoading}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="empty-state">
              <div className="empty-state-icon">🖼️</div>
              <h3>No Gallery Images</h3>
              <p>Add your first image to get started with the gallery.</p>
              <button 
                className="btn btn-primary"
                onClick={() => setShowAddForm(true)}
              >
                Add First Image
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default GalleryManagement
